/* eslint-disable */
import React from 'react';
import {
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Grid,
  InputLabel,
  CircularProgress,
} from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import { object, string } from 'yup';
import CloseIcon from '@material-ui/icons/Close';
import { ReactComponent as MoveToHappyLogo } from '../../assets/images/MTH-Color-logo.svg';
import { emailRegex, nameRegex } from '../../constants/regex';
import { showErrIfTouched } from '../../utils';
import useContactUs from '../../hooks/useContactUs';
import { useUser } from '../../contexts/UserContext';

const ContactUsModal = ({ open, onClose }) => {
  const maxCharacterCount = 700;
  const { createContactUs } = useContactUs();
  const { userData } = useUser();

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    // Trim the name before submission
    const cleanedValues = {
      ...values,
      name: values.name?.trim(),
    };

    const response = await createContactUs(cleanedValues);

    if (response?.status) {
      // Clear form immediately with empty values
      resetForm({
        values: {
          name: '',
          email: '',
          message: '',
        },
      });
      // Close modal after successful submission
      setTimeout(() => {
        handleClose();
      }, 100);
    } else {
    }

    setSubmitting(false);
  };

  // Helper function to clean name input in real-time
  const handleNameChange = (e, setFieldValue) => {
    let value = e.target.value;

    // Prevent leading spaces
    if (value.startsWith(' ')) {
      value = value.trimStart();
    }

    // Replace multiple consecutive spaces with single space
    value = value.replace(/\s{2,}/g, ' ');

    // Remove any non-letter/number characters except spaces (allows letters, numbers, and spaces)
    value = value.replace(/[^A-Za-z0-9\s]/g, '');

    setFieldValue('name', value);
  };

  // Helper function to clean message input in real-time
  const handleMessageChange = (e, setFieldValue) => {
    let value = e.target.value
      .replace(/[.\n]/g, ' ') // replace periods and newlines with space
      .replace(/\s{2,}/g, ' ') // collapse multiple spaces
      .trimStart(); // prevent leading spaces

    // Limit to 700 characters
    if (value.length > 700) {
      value = value.substring(0, 700);
    }

    setFieldValue('message', value);
  };

  // Get user data from context
  const getInitialValues = () => {
    return {
      name: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : '',
      email: userData?.emailId || '',
      message: '',
    };
  };

  // Validation schema using existing patterns
  const validationSchema = object().shape({
    name: string()
      .required('Name is required')
      .test('no-leading-space', 'Name cannot start with a space', (value) => {
        return value && !value.startsWith(' ');
      })
      .test('single-spaces-only', 'Only single spaces allowed between names', (value) => {
        return value && !/\s{2,}/.test(value); // No multiple consecutive spaces
      })
      .test(
        'valid-name-format',
        'Please enter a valid name (letters, numbers, and single spaces only).',
        (value) => {
          if (!value) return false;
          // Trim the value for validation but allow trailing spaces in the field
          const trimmedValue = value.trim();
          const isValid = nameRegex.test(trimmedValue);
          return isValid;
        },
      ),
    email: string()
      .required('Email is required')
      .matches(
        emailRegex,
        'Improper format: Please enter using the correct format (eg. name@email.com)',
      ),
    message: string()
      .required('Message is required')
      .min(10, 'Message must be at least 10 characters')
      .max(maxCharacterCount, `Message cannot exceed ${maxCharacterCount} characters`),
  });

  return (
    <Dialog
      className="CustomDialog ContactUsModal"
      aria-labelledby="contact-us-dialog-title"
      open={open}
      fullScreen
    >
      <MuiDialogTitle id="contact-us-dialog-title" className="CustomDialogTitle">
        {/* Header with Logo */}
        <Box className="ContactUsHeader">
          <Box className="ContactUsLogo">
            <MoveToHappyLogo />
          </Box>
          <Typography variant="h1" component="h1" className="ContactUsTitle" color="primary">
            Contact us
          </Typography>

          <Typography variant="body1" className="ContactUsSubtitle">
            Let us know how we can help. Our team will respond as soon as we can.
          </Typography>
        </Box>
        <Box className="CustomCloseIconSecond" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </Box>
      </MuiDialogTitle>

      <MuiDialogContent className="CustomDialogContent">
        <Box className="ContactUsContainer">
          {/* Main Content */}
          <Box className="ContactUsContent">
            {/* Contact Form */}
            <Formik
              key={open ? 'open' : 'closed'} // Force re-initialization when modal opens
              initialValues={getInitialValues()}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {(formikBag) => {
                const {
                  handleSubmit,
                  setFieldValue,
                  values,
                  errors,
                  touched,
                  isValid,
                  dirty,
                  isSubmitting: formikIsSubmitting,
                } = formikBag;
                return (
                  <Form className="ContactUsForm" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <InputLabel className="InputLabelFld" htmlFor="name">
                          Name
                          <Box mb={1} component="span" display="inline-block" className="Asterisk">
                            *
                          </Box>
                        </InputLabel>
                        <Field
                          as={TextField}
                          id="name"
                          name="name"
                          variant="outlined"
                          fullWidth
                          error={!!showErrIfTouched('name', errors, touched)}
                          helperText={showErrIfTouched('name', errors, touched)}
                          onChange={(e) => handleNameChange(e, setFieldValue)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <InputLabel className="InputLabelFld" htmlFor="email">
                          Email
                          <Box mb={1} component="span" display="inline-block" className="Asterisk">
                            *
                          </Box>
                        </InputLabel>
                        <Field
                          as={TextField}
                          id="email"
                          name="email"
                          type="email"
                          variant="outlined"
                          fullWidth
                          error={!!showErrIfTouched('email', errors, touched)}
                          helperText={showErrIfTouched('email', errors, touched)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <InputLabel className="InputLabelFld" htmlFor="message">
                          Message
                          <Box mb={1} component="span" display="inline-block" className="Asterisk">
                            *
                          </Box>
                        </InputLabel>
                        <Field
                          as={TextField}
                          name="message"
                          value={values.message}
                          className="TextFieldTextarea"
                          fullWidth
                          multiline
                          minRows={8}
                          maxLength={700}
                          label=""
                          variant="outlined"
                          placeholder="Type your message..."
                          onChange={(e) => handleMessageChange(e, setFieldValue)}
                          // inputProps={{
                          //   maxLength: maxCharacterCount,
                          // }}
                          error={!!showErrIfTouched('message', errors, touched)}
                          helperText={
                            showErrIfTouched('message', errors, touched) ||
                            `${maxCharacterCount - (values.message?.length || 0)}`
                          }
                        />
                        {/* Character count handled by CSS */}
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="large"
                          className="ContactUsSubmitButton"
                          disabled={!isValid || !dirty || formikIsSubmitting}
                          startIcon={
                            formikIsSubmitting ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : null
                          }
                        >
                          {formikIsSubmitting ? 'Sending...' : 'Submit'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </Box>
      </MuiDialogContent>
    </Dialog>
  );
};

export default ContactUsModal;
